import globalMessage from "@/utils/globalMessage";
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Avatar, Button, Comment, Form, Input, List, Spin, Tooltip, Badge } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";
import _ from "lodash";
import { api } from "@redux/api";

import "./WorkorderComment.css";
import { EventName, useEventBus } from "@/hooks/useEventBus";
import MessageEditor from "./MesssageEditor";
import ImagePreviewModal from "@components/ImagePreviewModal/ImagePreviewModal";
import AttachmentDisplay from "@components/AttachmentDisplay/AttachmentDisplay";

const { TextArea } = Input;

const CommentContent = React.memo(({ comment, userId, onDelete, onImagePreview }) => {
  const deleteWorkorderComment = useCallback(() => {
    onDelete(comment.id);
  }, [comment.id, onDelete]);

  const isCurrentUser = comment.createdby === userId;
  const userImage = _.get(comment, "createdby_image");
  
  // 附件和图片标识符
  const ATTACHMENT_PREFIX = '[ATTACHMENT]';
  const IMAGE_PREFIX = '[IMG]';

  // 处理图片双击事件
  const handleImageDoubleClick = useCallback((e) => {
    if (e.target.tagName === 'IMG' && e.target.src && onImagePreview) {
      onImagePreview(e.target.src);
    }
  }, [onImagePreview]);

  // 处理消息内容，区分附件、图片和普通文本
  const renderMessageContent = useCallback(() => {
    const contents = _.get(comment, "contents", []);
    const content = _.get(comment, "content", ""); // 兼容旧格式
    
    // 检查是否为数组格式（新格式）
    if (Array.isArray(contents) && contents.length > 0) {
      // 检查是否只有文本内容（没有图片和附件）
      const hasOnlyText = contents.every(item => 
        !item.startsWith(IMAGE_PREFIX) && !item.startsWith(ATTACHMENT_PREFIX)
      );
      
      if (hasOnlyText) {
        // 只有文本内容，使用 message-bubble
        return (
          <div className="message-bubble">
            <div className="message-content">
              {contents.map((item, index) => (
                <div key={index} className="message-text">
                  {item}
                </div>
              ))}
            </div>
          </div>
        );
      } else {
        // 包含图片或附件，不使用 message-bubble
        return (
          <div className="message-content">
            {contents.map((item, index) => {
              if (item.startsWith(IMAGE_PREFIX)) {
                const imageUrl = item.substring(IMAGE_PREFIX.length);
                return (
                  <div key={index} className="message-image-wrapper">
                    <img 
                      src={imageUrl} 
                      alt="消息图片" 
                      className="message-image"
                      style={{ maxWidth: '100%', height: 'auto', margin: '5px 0', borderRadius: '4px', cursor: 'pointer' }}
                      onDoubleClick={() => onImagePreview(imageUrl)}
                    />
                  </div>
                );
              } else if (item.startsWith(ATTACHMENT_PREFIX)) {
                const attachmentUrl = item.substring(ATTACHMENT_PREFIX.length);
                return (
                  <div key={index}>
                    <AttachmentDisplay url={attachmentUrl} onImagePreview={onImagePreview} />
                  </div>
                );
              } else {
                // 普通文本
                return (
                  <div key={index} className="message-text">
                    {item}
                  </div>
                );
              }
            })}
          </div>
        );
      }
    }
    
    // 兼容旧格式：检查是否为附件消息
    if (content.startsWith(ATTACHMENT_PREFIX)) {
      const attachmentUrl = content.substring(ATTACHMENT_PREFIX.length);
      return <AttachmentDisplay url={attachmentUrl} onImagePreview={onImagePreview} />;
    }
    
    // 兼容旧格式：检查是否为图片消息
    if (content.startsWith(IMAGE_PREFIX)) {
      const imageUrl = content.substring(IMAGE_PREFIX.length);
      return (
        <div className="message-image-wrapper">
          <img 
            src={imageUrl} 
            alt="消息图片" 
            className="message-image"
            style={{ maxWidth: '100%', height: 'auto', margin: '5px 0', borderRadius: '4px', cursor: 'pointer' }}
            onDoubleClick={() => onImagePreview(imageUrl)}
          />
        </div>
      );
    }
    
    // 兼容旧格式：普通HTML内容，使用 message-bubble
    return (
      <div className="message-bubble">
        <div className="message-text">
          {content}
        </div>
      </div>
    );
  }, [comment, handleImageDoubleClick, onImagePreview]);

  return (
    <div
      className={`message-wrapper ${isCurrentUser ? "message-right" : "message-left"
        }`}
    >
      <div className="message-avatar">
        <Avatar
          size={40}
          src={userImage}
          icon={!userImage && <i className="fas fa-user" />}
          className={
            isCurrentUser ? "current-user-avatar" : "other-user-avatar"
          }
        />
        <span className="message-author">
          {_.get(comment, "createdby_name")}
        </span>
      </div>
      <div className="message-content">
        {renderMessageContent()}
        <div className="message-footer">
          <Tooltip
            title={moment(_.get(comment, "modified_date")).format(
              "YYYY-MM-DD HH:mm:ss"
            )}
          >
            <span className="message-time">
              {/* {moment(_.get(comment, "modified_date")).fromNow()} */}
              {moment(_.get(comment, "modified_date")).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          </Tooltip>
          {isCurrentUser && (
            <Tooltip title="Delete">
              <Button
                type="text"
                icon={<i className="fas fa-trash-alt" />}
                onClick={deleteWorkorderComment}
                className="delete-btn"
                style={{ width: 'unset' }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
});

const Editor = React.memo(({ onSubmit, workorderId, equipmentId }) => {
  const handleSend = useCallback((htmlContent) => {
    onSubmit(htmlContent);
  }, [onSubmit]);

  return (
    <MessageEditor 
      onSend={handleSend}
      placeholder=""
      workorderId={workorderId}
      equipmentId={equipmentId}
    />
  );
});

export const WorkorderComment = React.memo(({ id, boxHeight, equipmentId }) => {
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);

  const userId = useSelector((state) => state.Auth.userId);

  // 滚动到底部 - 只滚动 messages-container，不影响外层容器
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // 处理图片预览
  const handleImagePreview = useCallback((imageSrc) => {
    setPreviewImage(imageSrc);
    setPreviewVisible(true);
  }, []);

  // 关闭预览
  const handleClosePreview = useCallback(() => {
    setPreviewVisible(false);
    setPreviewImage('');
  }, []);

  const getWorkorderComments = useCallback((workorderId) => {
    let isSubscribed = true;

    const fetchComments = async () => {
      try {
        setLoading(true);
        if(!workorderId) {
          return;
        }
        const items = await api.getWorkorderComment(workorderId);

        if (!isSubscribed) return;

        if (_.get(items, "status") === 200) {
          let data = items?.data.results;
          if (data) {
            data.sort(
              (a, b) => new Date(a.created_date) - new Date(b.created_date)
            );
          }
                  setComments(data ? data : []);

          if (isSubscribed) {
            setTimeout(() => {
              scrollToBottom();
            }, 100);
          }
        } else {
          if (isSubscribed) {
            globalMessage["error"]({
              message: "error",
              description: _.get(items, "response.statusText"),
            });
          }
        }
      } catch (error) {
        if (isSubscribed) {
          console.error(error);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchComments();

    return () => {
      isSubscribed = false;
    };
  }, [scrollToBottom]);

  useEventBus(EventName.RECIVED_WORKORDER_MESSAGE, (data) => {
    if (id === data.parent_id) {
      if (data.type === 'kpiworkordercomment_created' && !loading) {
        getWorkorderComments(id);
      }
    }
  });

  useEffect(() => {
    let cleanup;
    if (id) {
      cleanup = getWorkorderComments(id);
    }
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [id, getWorkorderComments]);

  const deleteWorkorderComment = useCallback(
    (commentId) => {
      setLoading(true);
      api
        .deleteWorkorderComment(commentId)
        .then(() => getWorkorderComments(id))
        .finally(() => setLoading(false));
    },
    [id, getWorkorderComments]
  );

  const handleSubmit = useCallback(
    (content) => {
      setLoading(true);
      
      // 检查内容是否为数组格式（新格式）
      if (Array.isArray(content)) {
        // 使用新的接口，contents是数组
        api
          .createWorkorderCommentMany({ contents: content, workorder: id })
          .then((response) => getWorkorderComments(id))
          .catch((error) => console.error(error))
          .finally(() => setLoading(false));
      } else {
        // 兼容旧格式，使用原接口
        api
          .createWorkorderComment({ content, workorder: id })
          .then((response) => getWorkorderComments(id))
          .catch((error) => console.error(error))
          .finally(() => setLoading(false));
      }
    },
    [id, getWorkorderComments]
  );

  return (
    <div className="chat-container">
      <Spin spinning={loading}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: boxHeight ? boxHeight : "70vh",
          }}
        >
          <div className="chat-header">
            <div className="chat-header-content">
              <span className="chat-title">Comments</span>
              {/* <Badge
                count={comments.length}
                size="small"
                style={{ backgroundColor: "#52c41a" }}
              /> */}
            </div>
          </div>
          <div ref={containerRef} className="messages-container">
            {comments.map((comment) => (
              <CommentContent
                key={comment.id}
                comment={comment}
                userId={userId}
                onDelete={deleteWorkorderComment}
                onImagePreview={handleImagePreview}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
          <Editor 
            onSubmit={handleSubmit} 
            workorderId={id}
            equipmentId={equipmentId}
          />
        </div>
      </Spin>
      
              {/* Modal 图片预览 */}
        <ImagePreviewModal
          visible={previewVisible}
          src={previewImage}
          onClose={handleClosePreview}
        />
    </div>
  );
});
